import {toLonLat, fromLonLat} from 'ol/proj';
import {geocoderUrl, env} from './config.js';
import autoComplete from '@tarekraafat/autocomplete.js';
import '@tarekraafat/autocomplete.js/dist/css/autoComplete.css';

const groups = [
  {
    label: 'Maps',
    pages: [
      { href: '/', label: 'Surface Map', id: 'main' },
      { href: '/surfacemap.html', label: 'Terrain', id: 'surfacemap' },
      { href: '/sealevel.html', label: 'Sea Level', id: 'sealevel' },
      { href: '/weather.html', label: 'Weather', id: 'weather' },
    ],
  },
  {
    label: 'Hikes',
    pages: [
      { href: '/hikes.html', label: 'Hikes', id: 'hikes' },
      { href: '/planner.html', label: 'Planner', id: 'planner' },
      { href: '/hikes3d.html', label: 'Hikes 3D', id: 'hikes3d' },
    ],
  },
];

const pages = groups.flatMap(function(g) { return g.pages; });

function getCurrentPageId() {
  const path = window.location.pathname;
  if (path === '/' || path === '/index.html') return 'main';
  const match = path.match(/\/(\w+)\.html/);
  return match ? match[1] : 'main';
}

function buildNavUrl(href) {
  const params = new URLSearchParams(window.location.search);
  const mapParams = ['z', 'lat', 'lon', 'hike'];
  const kept = new URLSearchParams();
  mapParams.forEach(function(k) {
    if (params.has(k)) kept.set(k, params.get(k));
  });
  const qs = kept.toString();
  return qs ? href + (href.includes('?') ? '&' : '?') + qs : href;
}

/** Create a dropdown wrapper for the nav bar. Returns {wrap, dropdown, toggle}. */
// Dropdowns open as a fixed overlay under their button, kept on screen: a
// menu near the right edge shifts left, and a long one scrolls. They live in
// <body>, not in the nav bar, so a nav bar that scrolls sideways on a phone
// (and whose backdrop blur would make it their containing block) cannot clip
// them.
const EDGE_PX = 4;

// On a phone a dropdown is a sheet along the bottom instead (see styles.css),
// over a backdrop that takes the tap meant to close it, so the tap does not
// also reach the map.
const PHONE = window.matchMedia('(max-width: 600px)');
let backdrop = null;

function syncBackdrop() {
  const open = PHONE.matches && document.querySelector('.nav-dropdown:not(.hidden)');
  if (!backdrop) {
    if (!open) return;
    backdrop = document.createElement('div');
    backdrop.className = 'nav-backdrop';
    backdrop.addEventListener('click', function () { closeDropdowns(null); });
    document.body.appendChild(backdrop);
  }
  backdrop.hidden = !open;
}

function placeDropdown(toggle, dropdown) {
  if (PHONE.matches) {
    dropdown.style.top = dropdown.style.left = '';
    return;
  }
  var r = toggle.getBoundingClientRect();
  dropdown.style.top = r.bottom + 'px';
  dropdown.style.left = '0px';
  var w = dropdown.offsetWidth;
  var left = Math.min(r.left, window.innerWidth - w - EDGE_PX);
  dropdown.style.left = Math.max(EDGE_PX, left) + 'px';
}

function closeDropdowns(except) {
  document.querySelectorAll('.nav-dropdown').forEach(function (d) {
    if (d !== except) d.classList.add('hidden');
  });
  syncBackdrop();
}

// The button it hangs from moves when the nav bar scrolls or the window
// resizes; close rather than follow.
window.addEventListener('resize', function () { closeDropdowns(null); });
document.addEventListener('scroll', function (e) {
  if (e.target && e.target.id === 'app-nav') closeDropdowns(null);
}, true);

export function createDropdown(label) {
  var wrap = document.createElement('div');
  wrap.className = 'nav-dropdown-wrap';

  var toggle = document.createElement('a');
  toggle.href = '#';
  if (typeof label === 'object' && label.icon) {
    var icon = document.createElement('i');
    icon.className = label.icon;
    toggle.appendChild(icon);
    if (label.text) {
      toggle.appendChild(document.createTextNode(' ' + label.text));
    }
  } else {
    toggle.textContent = label;
  }
  toggle.addEventListener('click', function (e) {
    e.preventDefault();
    closeDropdowns(dropdown);
    dropdown.classList.toggle('hidden');
    if (!dropdown.classList.contains('hidden')) placeDropdown(toggle, dropdown);
    syncBackdrop();
  });
  wrap.appendChild(toggle);

  var dropdown = document.createElement('div');
  dropdown.className = 'nav-dropdown hidden';
  document.body.appendChild(dropdown);
  // So whoever removes the button can remove its menu too.
  wrap.navDropdown = dropdown;

  // Close when clicking outside
  document.addEventListener('click', function (e) {
    if (!wrap.contains(e.target) && !dropdown.contains(e.target) && !dropdown.classList.contains('hidden')) {
      dropdown.classList.add('hidden');
      syncBackdrop();
    }
  });
  // A choice in a sheet closes it: the map is what you want to see next.
  dropdown.addEventListener('click', function (e) {
    if (PHONE.matches && e.target.closest('li, a')) closeDropdowns(null);
  });

  return {wrap, dropdown, toggle};
}

export function createNav() {
  const currentId = getCurrentPageId();

  const nav = document.createElement('nav');
  nav.id = 'app-nav';

  // Phones: the page links do not fit, so a menu button lists them and the
  // bar shows the current page's name. Hidden on wider screens.
  const menu = createDropdown({icon: 'fas fa-bars'});
  menu.wrap.classList.add('nav-menu');
  menu.toggle.setAttribute('aria-label', 'Pages');
  groups.forEach(function (group) {
    const h = document.createElement('div');
    h.className = 'nav-menu-group';
    h.textContent = group.label;
    menu.dropdown.appendChild(h);
    group.pages.forEach(function (page) {
      const a = document.createElement('a');
      a.className = 'nav-menu-link' + (page.id === currentId ? ' active' : '');
      a.textContent = page.label;
      a.addEventListener('click', function () { a.href = buildNavUrl(page.href); });
      a.href = buildNavUrl(page.href);
      menu.dropdown.appendChild(a);
    });
  });
  nav.appendChild(menu.wrap);
  const title = document.createElement('span');
  title.className = 'nav-title';
  const current = pages.find(function (p) { return p.id === currentId; });
  title.textContent = current ? current.label : '';
  nav.appendChild(title);

  groups.forEach(function(group, i) {
    if (i > 0) {
      var sep = document.createElement('div');
      sep.className = 'nav-sep';
      nav.appendChild(sep);
    }
    group.pages.forEach(function(page) {
      const a = document.createElement('a');
      a.href = buildNavUrl(page.href);
      a.textContent = page.label;
      if (page.id === currentId) {
        a.classList.add('active');
      }
      nav.appendChild(a);
    });
  });

  // Search input (pushed to the right)
  const searchWrap = document.createElement('div');
  searchWrap.className = 'nav-search';
  const input = document.createElement('input');
  input.id = 'autoComplete';
  input.type = 'search';
  input.autocomplete = 'off';
  searchWrap.appendChild(input);
  nav.appendChild(searchWrap);

  document.body.prepend(nav);
  return nav;
}

/**
 * Add a "Places" dropdown to the nav bar.
 * items: array of {name, ...} objects. onSelect(item) called on click.
 */
var placesWrap = null;

export function setupNavPlaces(items, onSelect) {
  var nav = document.getElementById('app-nav');
  if (!nav) return;

  // Remove existing places dropdown if re-called
  if (placesWrap && placesWrap.parentNode) {
    placesWrap.parentNode.removeChild(placesWrap);
    placesWrap.navDropdown.remove();
  }

  var {wrap, dropdown} = createDropdown({icon: 'fas fa-map-marker-alt'});
  placesWrap = wrap;

  var ul = document.createElement('ul');
  items.forEach(function (item) {
    var li = document.createElement('li');
    li.textContent = item.properties ? (item.properties.label || item.properties.name) : item.name;
    li.addEventListener('click', function () {
      dropdown.classList.add('hidden');
      onSelect(item);
    });
    ul.appendChild(li);
  });
  dropdown.appendChild(ul);

  // Insert right after the active page link
  var activeLink = nav.querySelector('a.active');
  var ref = activeLink ? activeLink.nextSibling : nav.firstChild;
  nav.insertBefore(wrap, ref);
}

/**
 * Add a "Layers" dropdown to the nav bar.
 * layers: array of {id, label, checked, group} objects; entries with a group
 * become radio buttons (one visible per group). {sep: true} draws a divider.
 * Returns object with input elements keyed by id.
 */
export function setupNavLayers(layers) {
  var nav = document.getElementById('app-nav');
  if (!nav) return {};

  var {wrap, dropdown} = createDropdown({icon: 'fas fa-layer-group'});
  var checkboxes = {};

  layers.forEach(function (layer) {
    if (layer.sep) {
      dropdown.appendChild(document.createElement('hr'));
      return;
    }
    if (layer.options) {
      // A setting of the layer above, e.g. the contour interval.
      var row = document.createElement('label');
      row.className = 'nav-option';
      var select = document.createElement('select');
      select.id = layer.id;
      layer.options.forEach(function (o) {
        var opt = document.createElement('option');
        opt.value = o[0];
        opt.textContent = o[1];
        select.appendChild(opt);
      });
      select.value = layer.value;
      row.appendChild(document.createTextNode(layer.label + ' '));
      row.appendChild(select);
      dropdown.appendChild(row);
      checkboxes[layer.id] = select;
      return;
    }
    var label = document.createElement('label');
    var cb = document.createElement('input');
    cb.type = layer.group ? 'radio' : 'checkbox';
    if (layer.group) cb.name = layer.group;
    cb.id = layer.id;
    cb.checked = layer.checked !== false;
    label.appendChild(cb);
    label.appendChild(document.createTextNode(' ' + layer.label));
    dropdown.appendChild(label);
    checkboxes[layer.id] = cb;
  });

  // Insert as first element
  var sep = document.createElement('div');
  sep.className = 'nav-sep';
  nav.insertBefore(sep, nav.firstChild);
  nav.insertBefore(wrap, nav.firstChild);

  return checkboxes;
}

/**
 * Setup geocoder search in nav bar.
 * onSelect receives {lat, lon, formatted}.
 */
export function setupNavSearch(onSelect) {
  const ac = new autoComplete({
    placeHolder: "Search location...",
    threshold: 3,
    searchEngine: "loose",
    data: {
      src: async (query) => {
        try {
          const source = await fetch(geocoderUrl(query), {
            method: 'GET',
            headers: { 'X-Authorization': `Apikey ${env.geocoder.apikey}` },
          });
          const data = await source.json();
          return data.results;
        } catch (error) {
          return error;
        }
      },
      keys: ["formatted"],
      cache: false,
    },
    resultItem: { highlight: true },
    events: {
      input: {
        selection: (event) => {
          const selection = event.detail.selection.value;
          ac.input.value = selection.formatted;
          onSelect({
            lat: selection.geometry.lat,
            lon: selection.geometry.lng,
            formatted: selection.formatted,
          });
        }
      }
    }
  });
  return ac;
}

/**
 * Save current map center/zoom to URL params so navigation preserves location.
 */
export function syncMapToUrl(map) {
  function updateUrl() {
    const view = map.getView();
    const center = view.getCenter();
    if (!center) return;
    const lonlat = toLonLat(center);
    const z = Math.round(view.getZoom());
    const url = new URL(window.location);
    url.searchParams.set('lat', lonlat[1].toFixed(5));
    url.searchParams.set('lon', lonlat[0].toFixed(5));
    url.searchParams.set('z', z);
    window.history.replaceState(null, '', url);
  }

  map.getView().on('change:center', updateUrl);
  map.getView().on('change:resolution', updateUrl);

  map.getView().on('change:center', function() {
    const navLinks = document.querySelectorAll('#app-nav a');
    navLinks.forEach(function(a) {
      const page = pages.find(function(p) { return a.textContent === p.label; });
      if (page) a.href = buildNavUrl(page.href);
    });
  });
}

/**
 * Read lat/lon/z from URL params and apply to view.
 */
export function restoreMapFromUrl(view) {
  const params = new URLSearchParams(window.location.search);
  const lat = parseFloat(params.get('lat'));
  const lon = parseFloat(params.get('lon'));
  const z = parseInt(params.get('z'));

  if (!isNaN(lat) && !isNaN(lon)) {
    view.setCenter(fromLonLat([lon, lat]));
    if (!isNaN(z)) view.setZoom(z);
    return true;
  }
  return false;
}
